import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, ApiError, apiDownloadUrl } from "../lib/api";
import type { CardBranding, Employee, WalletProviderStatus } from "../lib/types";

type Tab = "card" | "share" | "apple" | "google" | "samsung" | "qr" | "data";

const TABS: Array<[Tab, string]> = [
  ["card", "Business Card"],
  ["share", "Share Link"],
  ["apple", "Apple Wallet"],
  ["google", "Google Wallet"],
  ["samsung", "Samsung Wallet"],
  ["qr", "QR"],
  ["data", "Contact Data"],
];

interface ShareLink {
  url: string;
  slug: string;
  status?: string;
}

export default function EmployeeCard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [branding, setBranding] = useState<CardBranding | null>(null);
  const [providers, setProviders] = useState<WalletProviderStatus[]>([]);
  const [tab, setTab] = useState<Tab>("card");
  const [payload, setPayload] = useState<{ vcard: string } | null>(null);
  const [actionMessage, setActionMessage] = useState<{ type: "error" | "info"; text: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [shareLink, setShareLink] = useState<ShareLink | null>(null);

  function reload() {
    if (!id) return;
    api<{ employee: Employee }>(`/employees/${id}`).then((res) => setEmployee(res.employee));
  }

  useEffect(() => {
    reload();
    api<{ branding: CardBranding }>("/branding").then((res) => setBranding(res.branding));
    api<{ providers: WalletProviderStatus[] }>("/wallet/status").then((res) => setProviders(res.providers));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (tab === "data" && id) {
      api<{ vcard: string }>(`/employees/${id}/qr/payload`).then(setPayload);
    }
    // The slug is created lazily on first read, so don't request it until
    // an administrator actually opens the tab.
    if (tab === "share" && id && !shareLink) {
      api<ShareLink>(`/employees/${id}/share-link`).then(setShareLink);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, id]);

  if (!employee || !branding) return <div className="empty-state">Loading…</div>;

  const providerStatus = (name: string) => providers.find((p) => p.provider === name);

  async function handleGenerate(provider: "apple" | "google" | "samsung", regenerate: boolean) {
    setBusy(provider);
    setActionMessage(null);
    try {
      const res = await api<{ success: boolean; saveUrl?: string; downloadUrl?: string }>(
        `/wallet/employees/${id}/wallet/${provider}/${regenerate ? "regenerate" : "generate"}`,
        { method: "POST" }
      );
      if (res.downloadUrl) {
        window.location.href = res.downloadUrl;
      } else if (res.saveUrl) {
        window.open(res.saveUrl, "_blank", "noopener");
      }
      setActionMessage({ type: "info", text: `${provider} Wallet pass generated.` });
      reload();
    } catch (err) {
      const message = err instanceof ApiError ? err.message ?? err.code : "Failed to generate pass.";
      setActionMessage({ type: "error", text: message });
    } finally {
      setBusy(null);
    }
  }

  async function handleStatusChange(status: string) {
    // Suspending/archiving is not just a label change — it takes the
    // employee's share link offline, so say so before doing it.
    const warning =
      status === "ACTIVE"
        ? "Reactivate this card? Its share link starts working again, and any Wallet pass it had before is re-issued."
        : `Set this card to ${status}? Its share link stops working immediately — anyone who already has the link will see "card unavailable".`;
    if (!confirm(warning)) return;
    await api(`/employees/${id}/status`, { method: "PATCH", body: { status } });
    reload();
  }

  async function handleDelete() {
    if (!employee) return;
    if (
      !confirm(
        `Permanently delete ${employee.displayName}?\n\n` +
          "This erases the employee record, their photo, and their wallet pass history from the database. " +
          "It cannot be undone. Passes already saved to someone's phone cannot be recalled.\n\n" +
          "If you only want to stop the card working, use Archive instead."
      )
    )
      return;
    setBusy("delete");
    setActionMessage(null);
    try {
      await api(`/employees/${id}`, { method: "DELETE" });
      navigate("/employees");
    } catch (err) {
      const message = err instanceof ApiError ? err.message ?? err.code : "Failed to delete the employee.";
      setActionMessage({ type: "error", text: message });
      setBusy(null);
    }
  }

  return (
    <div>
      <div className="topbar">
        <div>
          <h1 className="page-title">{employee.displayName}</h1>
          <div className="page-subtitle">
            {employee.jobTitle} · {employee.companyName} ·{" "}
            <span className={`badge badge-${employee.status.toLowerCase()}`}>{employee.status}</span>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Link className="btn btn-secondary" to={`/employees/${id}/edit`}>
            Edit Employee
          </Link>
          <Link className="btn btn-secondary" to="/employees">
            Back
          </Link>
        </div>
      </div>

      {actionMessage && <div className={`alert alert-${actionMessage.type}`}>{actionMessage.text}</div>}

      <div className="tabs">
        {TABS.map(([key, label]) => (
          <button key={key} className={`tab ${tab === key ? "active" : ""}`} onClick={() => setTab(key)}>
            {label}
          </button>
        ))}
      </div>

      {tab === "card" && (
        <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
          <div
            className="business-card-preview"
            style={{
              background: branding.primaryColor,
              color: branding.textColor,
            }}
          >
            {employee.photo?.cardUrl && (
              <img src={employee.photo.cardUrl} alt="" style={{ width: 64, height: 64, borderRadius: "50%", objectFit: "cover", marginBottom: 12 }} />
            )}
            <div className="name">{employee.displayName}</div>
            <div className="title" style={{ color: branding.secondaryColor }}>
              {employee.jobTitle}
            </div>
            <div className="company">{employee.companyName}</div>
            {employee.mobile && <div className="contact-line">{employee.mobile}</div>}
            {employee.email && <div className="contact-line">{employee.email}</div>}
          </div>
          <div className="card" style={{ flex: 1, minWidth: 260 }}>
            <h3 style={{ marginTop: 0 }}>Actions</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, alignItems: "flex-start" }}>
              <a className="btn btn-secondary" href={apiDownloadUrl(`/employees/${id}/qr?format=png&size=1024`)}>
                Download Contact QR (PNG)
              </a>
              <a className="btn btn-secondary" href={apiDownloadUrl(`/employees/${id}/qr?format=svg`)}>
                Download Contact QR (SVG)
              </a>
              <a className="btn btn-secondary" href={apiDownloadUrl(`/employees/${id}/vcard`)}>
                Download Contact (.vcf)
              </a>
              <a className="btn btn-secondary" href={apiDownloadUrl(`/employees/${id}/print-card?side=front`)} target="_blank" rel="noreferrer">
                Print Card — Front
              </a>
              <a className="btn btn-secondary" href={apiDownloadUrl(`/employees/${id}/print-card?side=back`)} target="_blank" rel="noreferrer">
                Print Card — Back (QR)
              </a>
              <a className="btn btn-secondary" href={apiDownloadUrl(`/employees/${id}/package`)}>
                Download Full Kit (.zip)
              </a>
              <div style={{ borderTop: "1px solid var(--color-border)", width: "100%", margin: "8px 0" }} />
              {/* Archiving is a soft, reversible state (only permanent
                  deletion is final), so an archived card keeps its way
                  back to ACTIVE — the API allows the transition either
                  way. */}
              <button
                className="btn btn-secondary"
                onClick={() => handleStatusChange(employee.status === "ACTIVE" ? "SUSPENDED" : "ACTIVE")}
              >
                {employee.status === "ACTIVE"
                  ? "Suspend Card"
                  : employee.status === "ARCHIVED"
                    ? "Reactivate Card"
                    : "Activate Card"}
              </button>
              {employee.status !== "ARCHIVED" && (
                <button className="btn btn-danger" onClick={() => handleStatusChange("ARCHIVED")}>
                  Archive / Deactivate Card
                </button>
              )}
              {/* Permanent deletion is deliberately reachable only from the
                  archived state — the API enforces the same rule, so the
                  button would 409 otherwise. */}
              {employee.status === "ARCHIVED" ? (
                <button className="btn btn-danger" disabled={busy === "delete"} onClick={handleDelete}>
                  {busy === "delete" ? "Deleting…" : "Delete Permanently"}
                </button>
              ) : (
                <span className="muted" style={{ fontSize: 12 }}>
                  To delete this employee permanently, archive the card first.
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {tab === "share" && (
        <ShareTab
          link={shareLink}
          employee={employee}
          busy={busy === "share"}
          onRotate={async () => {
            if (!confirm("Rotate the link? The link you already sent to this employee stops working immediately.")) return;
            setBusy("share");
            setActionMessage(null);
            try {
              setShareLink(await api<ShareLink>(`/employees/${id}/share-link/rotate`, { method: "POST" }));
              setActionMessage({ type: "info", text: "A new link was issued. The previous one no longer works." });
            } catch (err) {
              const message = err instanceof ApiError ? err.message ?? err.code : "Failed to rotate the link.";
              setActionMessage({ type: "error", text: message });
            } finally {
              setBusy(null);
            }
          }}
        />
      )}

      {(["apple", "google", "samsung"] as const).includes(tab as never) && (
        <WalletTab
          provider={tab as "apple" | "google" | "samsung"}
          status={providerStatus(tab)}
          employee={employee}
          busy={busy === tab}
          onGenerate={(regen) => handleGenerate(tab as "apple" | "google" | "samsung", regen)}
        />
      )}

      {tab === "qr" && (
        <div className="card" style={{ maxWidth: 480 }}>
          <div className="alert alert-warning">Anyone who scans this QR can read the contact details encoded in it.</div>
          <img src={apiDownloadUrl(`/employees/${id}/qr?format=png&size=512`)} alt="Contact QR" style={{ width: "100%", maxWidth: 320 }} />
        </div>
      )}

      {tab === "data" && (
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Raw contact payload (encoded in the QR / VCF / Wallet barcode)</h3>
          <pre style={{ whiteSpace: "pre-wrap", background: "#f5f6f8", padding: 12, borderRadius: 8, fontSize: 12 }}>{payload?.vcard ?? "Loading…"}</pre>
        </div>
      )}
    </div>
  );
}

/**
 * The distribution surface (spec section 20): an administrator never adds
 * a pass on behalf of an employee — they hand over a link, and the
 * employee's own phone does the rest.
 */
function ShareTab({
  link,
  employee,
  busy,
  onRotate,
}: {
  link: ShareLink | null;
  employee: Employee;
  busy: boolean;
  onRotate: () => void;
}) {
  const [copied, setCopied] = useState(false);

  if (!link) return <div className="empty-state">Loading…</div>;

  const inactive = employee.status !== "ACTIVE";
  const whatsappText = `${employee.displayName} — ${employee.jobTitle}, ${employee.companyName}\n${link.url}`;

  async function copy() {
    try {
      await navigator.clipboard.writeText(link!.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }

  return (
    <div className="card" style={{ maxWidth: 620 }}>
      <h3 style={{ marginTop: 0 }}>Share link</h3>
      <p className="muted">
        Send this to the employee (WhatsApp, SMS, email). Opening it on their phone shows their card with an
        &ldquo;Add to Apple Wallet&rdquo; button, a Google Wallet button once that provider is configured, and a
        &ldquo;Save to Contacts&rdquo; download for every other device.
      </p>

      {inactive && (
        <div className="alert alert-warning">
          This card is <strong>{employee.status}</strong>. The link resolves to &ldquo;card unavailable&rdquo; until the
          card is set back to ACTIVE.
        </div>
      )}

      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          background: "#f5f6f8",
          border: "1px solid var(--color-border)",
          borderRadius: 8,
          padding: "10px 12px",
          marginBottom: 12,
        }}
      >
        <code style={{ flex: 1, fontSize: 13, wordBreak: "break-all" }}>{link.url}</code>
      </div>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 20 }}>
        <button className="btn btn-gold" onClick={copy}>
          {copied ? "Copied ✓" : "Copy link"}
        </button>
        <a
          className="btn btn-secondary"
          href={`https://wa.me/?text=${encodeURIComponent(whatsappText)}`}
          target="_blank"
          rel="noreferrer"
        >
          Send on WhatsApp
        </a>
        <a className="btn btn-secondary" href={link.url} target="_blank" rel="noreferrer">
          Preview
        </a>
        <button className="btn btn-danger" disabled={busy} onClick={onRotate}>
          {busy ? "Working…" : "Rotate link"}
        </button>
      </div>

      <div style={{ borderTop: "1px solid var(--color-border)", paddingTop: 16 }}>
        <h4 style={{ margin: "0 0 8px" }}>Scan to open on a phone</h4>
        <p className="muted" style={{ marginTop: 0 }}>
          This QR encodes the share link above — not the contact data. Use the QR tab for the offline contact code
          that goes on printed cards.
        </p>
        <img
          src={apiDownloadUrl(`/employees/${employee.id}/share-link/qr.png?v=${link.slug}`)}
          alt="Share link QR"
          style={{ width: 190, height: 190, background: "#fff", borderRadius: 8, padding: 8 }}
        />
      </div>
    </div>
  );
}

function WalletTab({
  provider,
  status,
  employee,
  busy,
  onGenerate,
}: {
  provider: "apple" | "google" | "samsung";
  status?: WalletProviderStatus;
  employee: Employee;
  busy: boolean;
  onGenerate: (regenerate: boolean) => void;
}) {
  const pass = employee.walletPasses?.find((p) => p.provider === provider);
  const ready = status?.status === "READY";
  const label = provider === "apple" ? "Apple Wallet" : provider === "google" ? "Google Wallet" : "Samsung Wallet";

  return (
    <div className="card" style={{ maxWidth: 560 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <h3 style={{ margin: 0 }}>{label}</h3>
        <span className={`badge badge-${(status?.status ?? "NOT_CONFIGURED").toLowerCase().replace(/_/g, "-")}`}>
          {status?.status ?? "UNKNOWN"}
        </span>
      </div>

      {!ready && <div className="alert alert-warning">{status?.message ?? "This Wallet provider is not yet configured."}</div>}

      <p className="muted">This preview is a simulation and is not pixel-identical to the real Wallet app rendering.</p>

      <div
        className="business-card-preview"
        style={{ background: "#1c1c1e", color: "#fff", maxWidth: 320, marginBottom: 16 }}
      >
        <div className="name">{employee.displayName}</div>
        <div className="title">{employee.jobTitle}</div>
        <div className="company">{employee.companyName}</div>
      </div>

      {pass && (
        <p className="muted">
          Status: {pass.status}
          {pass.errorMessage ? ` — ${pass.errorMessage}` : ""}
          {pass.generatedAt ? ` — last generated ${new Date(pass.generatedAt).toLocaleString()}` : ""}
        </p>
      )}

      <div style={{ display: "flex", gap: 10 }}>
        <button className="btn btn-gold" disabled={busy} onClick={() => onGenerate(false)}>
          {busy ? "Working…" : `Add to ${label}`}
        </button>
        {pass?.status === "GENERATED" && (
          <button className="btn btn-secondary" disabled={busy} onClick={() => onGenerate(true)}>
            Regenerate Pass
          </button>
        )}
      </div>
    </div>
  );
}
